<script>
  // import input formatting functions
  import { formatCurrency, formatPercentage } from '../util';
  // calculate payment function
  import calculateAffordability from './calculateAffordability';
  // import calculator base  components
  import CalculatorDisclaimer from '../CalculatorDisclaimer.svelte';
  import CalculatorFieldError from '../CalculatorFieldError.svelte';
  import CalculatorHeader from '../CalculatorHeader.svelte';
  import CalculatorSubmitInput from '../CalculatorSubmitInput';

  // company prop for passing to Disclaimer
  export let company;

  // fields
  let annualInterestRate = {
    value: '',
    touched: false,
    error: false,
    errorMessage: 'Please enter a value',
  };
  let termOfTheLoan = {
    value: '30',
    touched: false,
    error: false,
    errorMessage: 'Please select the Term of the Loan',
  };
  let annualRealEstateTaxes = {
    value: '',
    touched: false,
    error: false,
    errorMessage: 'Please enter a value',
  };
  let annualHomeownersInsurance = {
    value: '',
    touched: false,
    error: false,
    errorMessage: 'Please enter a value',
  };
  let grossAnnualIncome = {
    value: '',
    touched: false,
    error: false,
    errorMessage: 'Please enter a value',
  };
  let monthlyDebtObligations = {
    value: '',
    touched: false,
    error: false,
    errorMessage: 'Please enter a value',
  };

  // result values
  let maximumMonthlyMortgagePayment = 0;
  let maximumLoanAmount = 0;

  // has the result been calculated?
  let calculated = false;

  // format percentage for annualInterestRate and update value
  function formatAnnualInterestRate() {
    // if no input entered, exit
    if (annualInterestRate.value === '') return;

    const formattedPercentage = formatPercentage(annualInterestRate.value);

    annualInterestRate.value = formattedPercentage;
  }

  // reactive statements to format input values when they change
  $: if (annualRealEstateTaxes.value !== '')
    annualRealEstateTaxes.value = formatCurrency(annualRealEstateTaxes.value);

  $: if (annualHomeownersInsurance.value !== '')
    annualHomeownersInsurance.value = formatCurrency(
      annualHomeownersInsurance.value
    );

  $: if (grossAnnualIncome.value !== '')
    grossAnnualIncome.value = formatCurrency(grossAnnualIncome.value);

  $: if (monthlyDebtObligations.value !== '')
    monthlyDebtObligations.value = formatCurrency(monthlyDebtObligations.value);

  // do required fields have a value?
  $: if (
    annualInterestRate.value !== '' &&
    annualInterestRate.value !== '0.000%'
  )
    annualInterestRate.error = false;

  $: if (grossAnnualIncome.value !== '' && grossAnnualIncome.value !== '$0')
    grossAnnualIncome.error = false;

  function setCalculatedAndCalculate() {
    // check if required fields have a value
    if (
      annualInterestRate.value === '' ||
      annualInterestRate.value === '0.000%'
    )
      annualInterestRate.error = true;

    if (grossAnnualIncome.value === '' || grossAnnualIncome.value === '$0')
      grossAnnualIncome.error = true;

    if (
      annualInterestRate.value === '0.000%' ||
      annualInterestRate.value === '' ||
      grossAnnualIncome.value === '$0' ||
      grossAnnualIncome.value === ''
    )
      return;

    calculated = true;

    // returned as array of [maximumMonthly, maximumAmount] strings
    const calculatedAffordability = calculateAffordability(
      annualInterestRate.value,
      termOfTheLoan.value,
      annualRealEstateTaxes.value,
      annualHomeownersInsurance.value,
      grossAnnualIncome.value,
      monthlyDebtObligations.value
    );

    maximumMonthlyMortgagePayment = calculatedAffordability[0];
    maximumLoanAmount = calculatedAffordability[1];
  }
</script>

<div>
  <CalculatorHeader
    title="Affordability Calculator"
    subtitle="Calculate How Much You Can Borrow" />
  <form on:submit|preventDefault={setCalculatedAndCalculate}>
    <div class="bg-gray-efftwo px-16 py-8">
      <div class="calculator-input-fields-wrapper">
        <div class="calculator-field lg:mr-16 -mb-12 lg:mb-0 ">
          <label for="annualInterestRate" class="block">
            Annual Interest Rate
          </label>
          <input
            type="text"
            id="annualInterestRate"
            name="annualInterestRate"
            placeholder="3.500%"
            on:blur={formatAnnualInterestRate}
            bind:value={annualInterestRate.value}
            class="mt-2" />
          <CalculatorFieldError field={annualInterestRate} />
        </div>
        <div class="calculator-field mb-12 lg:mr-16 lg:mb-0">
          <label for="termOfTheLoan" class="block">Term of the Loan</label>
          <select
            name="termOfTheLoan"
            id="termOfTheLoan"
            bind:value={termOfTheLoan.value}
            class="mt-2"
            required>
            <option value="30" selected>30 Years</option>
            <option value="25">25 Years</option>
            <option value="20">20 Years</option>
            <option value="15">15 Years</option>
            <option value="10">10 Years</option>
            <option value="5">5 Years</option>
          </select>
        </div>
        <div class="calculator-field mb-12 lg:mb-0">
          <label for="annualRealEstateTaxes" class="block">
            Annual Real Estate Taxes
          </label>
          <input
            type="text"
            id="annualRealEstateTaxes"
            name="annualRealEstateTaxes"
            placeholder="$500"
            bind:value={annualRealEstateTaxes.value}
            class="mt-2" />
        </div>
        <div class="calculator-field mb-12 lg:mr-16 lg:mb-0">
          <label for="annualHomeownersInsurance" class="block">
            Annual Homeowners Insurance
          </label>
          <input
            type="text"
            id="annualHomeownersInsurance"
            name="annualHomeownersInsurance"
            placeholder="$500"
            bind:value={annualHomeownersInsurance.value}
            class="mt-2" />
        </div>
        <div class="calculator-field lg:mr-16 -mb-12 lg:mb-0 ">
          <label for="grossAnnualIncome" class="block">
            Gross Annual Income
          </label>
          <input
            type="text"
            id="grossAnnualIncome"
            name="grossAnnualIncome"
            placeholder="$50,000"
            bind:value={grossAnnualIncome.value}
            class="mt-2" />
          <CalculatorFieldError field={grossAnnualIncome} />
        </div>
        <div class="calculator-field">
          <label for="monthlyDebtObligations" class="block">
            Monthly Debt Obligations
          </label>
          <input
            type="text"
            id="monthlyDebtObligations"
            name="monthlyDebtObligations"
            placeholder="$250"
            bind:value={monthlyDebtObligations.value}
            class="mt-2" />
        </div>
      </div>
    </div>
    <CalculatorSubmitInput />
  </form>

  <div class="mt-10">
    {#if calculated}
      <div class="calculator-results-wrapper">
        <div
          class="flex flex-col justify-between px-8 pt-8 lg:flex-row lg:px-16
          lg:py-8 lg:w-4/5">
          <div class="mb-16 lg:mb-0">
            <p class="calculator-result-title pl-0">
              Maximum Monthly Mortgage Payment
            </p>
            <p class="calculator-result-result text-25">
              ${maximumMonthlyMortgagePayment}
            </p>
          </div>
          <div>
            <p class="calculator-result-title pl-0">Maximum Loan Amount</p>
            <p class="calculator-result-result text-25">${maximumLoanAmount}</p>
          </div>
        </div>
        <div class="px-16">
          <CalculatorDisclaimer {company} />
        </div>
      </div>
    {/if}
  </div>
</div>



// WEBPACK FOOTER //
// ./scripts/svelte/calculators/affordabilityCalculator/AffordabilityCalculator.svelte